/**
 * **What happens when a session ends** — ADR-0012, adopting FiveOX ADR-0079
 * and ADR-0041.
 *
 * An access token is a 15-minute JWT that every service verifies *locally*
 * against JWKS. Nothing asks whether the session behind it still exists. So a
 * reconciler who signs out of another FiveOX app — which revokes the whole
 * login group, ours included — changes nothing this app can see: the stored
 * token still verifies, every request still succeeds, and a TDS Reco tab that
 * was closed at that moment reopens from `localStorage` and carries on. That is
 * the report the platform fixed this against, verbatim: *"logout from one app,
 * still logged in another"*.
 *
 * Three things have to be true, and this module is the one place that holds
 * all three:
 *
 * 1. **We ask.** `session.watch()` is the shell's watchdog (SESSION_WATCHDOG
 *    §3a): `GET /session/status` on start, on focus, on a bfcache restore and
 *    every 60s, coalesced. A 401/403 ends the session; a 5xx, a timeout or a
 *    thrown fetch does **not** — auth being unavailable must never sign anyone
 *    out, and here that would drop a reconciler mid-review with a maker–checker
 *    step somebody else is waiting on.
 * 2. **We listen, scoped.** The ~2s sign-out broadcast has existed estate-wide
 *    since 2026-08-14 and this app never registered for it at all. It does now
 *    — with its own user id, so that another app tidying up somebody *else's*
 *    dead session does not sign this person out.
 * 3. **We forget what the session claimed.** Both halves — see
 *    `clearsAppStateOnEnd` below. This is the part the platform does not do for
 *    us, in either half.
 *
 * The watchdog itself is emphatically **not** copied here. `lib/session.ts` is
 * `createSessionStore()`, so `watch()` is already a method on the store we use;
 * the platform's own document does not list this repo at all, and its §3a/§3b
 * split would have filed us under §3b if it had — a misfiling recorded in
 * ADR-0012 and owed back as a finding. Copying 110 lines would fork the policy
 * of what a dead session means, which is exactly what drifts.
 */

import { onSignOut } from '@fiveox/shell'

import { setOrgId } from './api'
import { session } from './session'
import { clearSessionState } from '../workspace/sessionReset'

/**
 * **Nothing the session claimed outlives it** (ADR-0012, decision 4).
 *
 * `broadcastSignOut()` clears the *shared parent-domain* org cookie, which
 * fixed this for the core apps. **We are outside that fix twice over**, and
 * both halves are cleared here:
 *
 * `fiveox.tdsreco_org` — ours, per-app deliberately, since storage is
 * per-origin and one app's selection must never be revived from another's.
 * Under ADR-0002 the org **is the CA firm**, so residue is not a dropdown
 * remembering the wrong value: it is the wrong firm's name in the bar and the
 * wrong firm's client list one click away, for whoever sits down next. A shared
 * workstation in a practice is the normal case.
 *
 * The **`tds.` workspace keys** — `tds.lastRun.<periodId>` and
 * `tds.openedSides`. `sessionReset.ts` was written against exactly this bug and
 * says so: they are sessionStorage, scoped to the TAB rather than to the login,
 * so signing out and back in without closing the tab left *"a previous
 * session's run and files already on screen"*. That module had **no production
 * call site** until this one — the app diagnosed the bug, wrote the fix, and
 * never called it. A watchdog-driven end without this would leak workspace
 * state in precisely the way that file exists to prevent, so the two are one
 * behaviour and ship together.
 *
 * **On the transition, not on the state.** Clearing whenever the session is
 * merely *absent* would also fire during the SSO round trip, when `/callback`
 * renders with no session for a moment before it stores the tokens — and would
 * quietly delete the remembered firm on every single sign-in, which
 * `resolveSelected` exists to honour, and blank the workspace of somebody who
 * never left. So this fires only when a session that **existed** has gone.
 */
function clearsAppStateOnEnd(): () => void {
  let had = session.getSession() !== null
  return session.subscribe(() => {
    const has = session.getSession() !== null
    if (had && !has) {
      setOrgId(null)
      clearSessionState(sessionStorage)
    }
    had = has
  })
}

/**
 * Arm all three. Mounted **once**, from the guard that already owns the
 * *no session → SSO* decision; the returned unsubscribe is what an effect's
 * cleanup wants.
 *
 * Nothing here navigates. The guard reads the session through
 * `useSyncExternalStore`, so ending it re-renders the app into `SsoRedirect` on
 * its own — and that is the authoritative path, because accounts, with a live
 * session, is what actually decides who this is.
 */
export function armSessionEnd(): () => void {
  const stopWatchdog = session.watch()
  const stopAppStateClear = clearsAppStateOnEnd()
  // `forUser` is read at the moment a broadcast arrives, never captured: a
  // refresh rotates the session and a captured id would be stale.
  const stopListener = onSignOut(
    () => session.clearSession(),
    () => session.getSession()?.userId ?? null,
  )
  return () => {
    stopWatchdog()
    stopAppStateClear()
    stopListener()
  }
}
